import Vue from "vue";
import VueRouter, { RawLocation, RouteConfig } from "vue-router";

import PreLogin from "../views/PreLogin.vue";
import Authenticate from "../views/Authenticate.vue";
import ForgotPassword from "../views/ForgotPassword.vue";
import PasswordLogin from "@/views/PasswordLogin.vue";

import Users from "../views/Users.vue";
import UserDetails from "../views/UserDetails.vue";
import UserCreate from "../views/UserCreate.vue";

import Connections from "../views/Connections.vue";

import Licences from "../views/Licences.vue";

import Settings from "../views/Settings.vue";

import NotFound from "../views/NotFound.vue";
import { isLoggedIn, LoginView } from "@micad/vue-components";
import ResetPasswordCallback from "@/views/ResetPasswordCallback.vue";
import Logout from "@/views/Logout.vue";
import UserSelfRegister from "@/views/UserSelfRegister.vue";
import Clients from "@/views/Clients.vue";
import ClientDetails from "@/views/ClientDetails.vue";
import ClientCreate from "@/views/ClientCreate.vue";
import Branding from "@/views/BrandingLogin.vue";
import TermsAndConditions from "@/views/TermsAndConditions.vue";
import Notifications from "@/views/NotificationsEditor.vue";
import HistoricAccount from "@/views/HistoricAccount.vue";
import ClientChooser from "@/views/ClientChooser.vue";
import ProfileEdit from "@/views/ProfileEdit.vue";
import ProfileList from "@/views/ProfileList.vue";
import MFALogin from "@/views/MFALogin.vue";
import OAuth from "@/views/OAuth.vue";
import OIDC from "@/views/OIDC.vue";
import ErrorView from "@/views/ErrorView.vue";
import { logUserActivity } from "@/data/repos/userActivity";
import { maskSensitiveData } from "@/modules/logMask";
import { getKV } from "@/modules/browserLocalStorage";
import { CLIENT_COOKIE_NAME } from "@/interfaces/common.interfaces";
import SendEmail from "@/views/SendEmail.vue";
import { switchClient } from "@/modules/utils";
import ErrorLicenseView from "@/views/ErrorLicenseView.vue";

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.popStateDetected = false;
window.addEventListener("popstate", () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.popStateDetected = true;
});

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: "/sso-callback",
    name: "sso-callback",
    props: (route) => ({ layout: "no-navigation" }),
    meta: { layout: "no-navigation" },
    component: PreLogin,
  },
  {
    path: "/pre-login",
    name: "pre-login",
    meta: { layout: "no-navigation" },
    component: PreLogin,
    props: true,
  },
  {
    path: "/register",
    name: "register",
    meta: { layout: "no-navigation" },
    component: UserSelfRegister,
    props: true,
  },
  {
    path: "/login",
    name: "login",
    props: (route) => ({
      ...route.params,
    }),
    meta: { layout: "no-navigation" },
    component: PasswordLogin,
  },
  {
    path: "/login/chooseClient",
    name: "chooseClient",
    props: (route) => ({
      ...route.params,
    }),
    meta: { layout: "no-navigation" },
    component: ClientChooser,
  },
  {
    path: "/login/switchClient",
    name: "SwitchClient",
    props: (route) => ({
      ...route.params,
      switch: true,
    }),
    meta: { layout: "no-navigation" },
    component: ClientChooser,
  },
  {
    path: "/login/mfa",
    name: "MFALogin",
    props: (route) => ({
      ...route.params,
    }),
    meta: { layout: "no-navigation" },
    component: MFALogin,
  },
  {
    path: "/legacy-account",
    name: "LegacyAccount",
    props: (route) => ({
      ...route.params,
    }),
    meta: { layout: "no-navigation" },
    component: HistoricAccount,
  },
  {
    path: "/login-redirect",
    name: "login-redirect",
    component: LoginView,
  },
  {
    path: "/authenticate",
    name: "authenticate",
    meta: { layout: "no-navigation" },
    component: Authenticate,
  },
  {
    path: "/forgot-password",
    name: "forgot-password",
    meta: { layout: "no-navigation" },
    component: ForgotPassword,
    props: true,
  },
  {
    path: "/reset-password",
    name: "reset-password-callback",
    meta: { layout: "no-navigation" },
    component: ResetPasswordCallback,
  },
  {
    path: "/",
    name: "Home",
    component: Users,
  },
  {
    path: "/user-create",
    name: "UserCreate",
    component: UserCreate,
  },
  {
    path: "/user/:id?",
    name: "UserDetails",
    component: UserDetails,
  },
  {
    path: "/email/send",
    name: "SendEmail",
    component: SendEmail,
    props: true,
  },
  {
    path: "/clients",
    name: "Clients",
    component: Clients,
  },
  {
    path: "/client/:key",
    name: "ClientDetails",
    component: ClientDetails,
  },
  {
    path: "/create-client/:parentKey?",
    name: "ClientCreate",
    component: ClientCreate,
    props: true,
  },
  {
    path: "/user-profile/create",
    name: "UserProfileCreate",
    component: ProfileEdit,
  },
  {
    path: "/user-profile/:id?",
    name: "UserProfileEdit",
    component: ProfileEdit,
  },
  {
    path: "/user-profiles",
    name: "UserProfiles",
    component: ProfileList,
  },
  {
    path: "/connections",
    name: "Connections",
    component: Connections,
  },
  {
    path: "/licences",
    name: "Licences",
    component: Licences,
  },
  {
    path: "/terms",
    name: "Terms",
    component: TermsAndConditions,
  },
  {
    path: "/notifications",
    name: "Notifications",
    component: Notifications,
  },
  {
    path: "/branding",
    name: "Branding",
    component: Branding,
  },
  {
    path: "/settings",
    name: "Settings",
    component: Settings,
  },
  {
    path: "/logout",
    name: "Logout",
    meta: { layout: "no-navigation" },
    component: Logout,
  },
  {
    path: "/oauth",
    name: "OAuth",
    meta: { layout: "no-navigation" },
    component: OAuth,
  },
  {
    path: "/oidc/authorize",
    name: "OIDC",
    meta: { layout: "no-navigation" },
    component: OIDC,
  },
  {
    path: "/error",
    name: "Error",
    meta: { layout: "no-navigation" },
    component: ErrorView,
  },
  {
    path: "/license-problem",
    name: "LicenseError",
    meta: { layout: "no-navigation" },
    component: ErrorLicenseView,
  },
  {
    path: "*",
    name: "404*",
    meta: { layout: "no-navigation" },
    component: NotFound,
  },
  // {
  //   path: "/about",
  //   name: "About",
  //   // route level code-splitting
  //   // this generates a separate chunk (about.[hash].js) for this route
  //   // which is lazy-loaded when the route is visited.
  //   component: () =>
  //     import(/* webpackChunkName: "about" */ "../views/About.vue"),
  // },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

export default router;

export const anonymousRoutes = [
  "pre-login",
  "register",
  "login",
  "branded-login",
  "Logout",
  "forgot-password",
  "reset-password-callback",
  "LegacyAccount",
  "chooseClient",
  "SwitchClient",
  "MFALogin",
  "OAuth",
  "OIDC",
  "Error",
];

router.beforeEach((to, from, next) => {
  // No other way to monitor global route changes without vuex...
  // currentRouteName.name = to.name || "";
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const IsItABackButton = window.popStateDetected;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.popStateDetected = false;
  if (IsItABackButton && from.params.refresh) {
    to.params.refresh = from.params.refresh;
  }

  if (!anonymousRoutes.includes(to.name || "") && !isLoggedIn)
    next({ name: "pre-login" });
  else next();
});

router.afterEach(async (to) => {
  try {
    logUserActivity(
      `${window.location.origin}${maskSensitiveData(to.fullPath)}`
    ).then();
  } catch (e) {
    console.log(e);
  }

  const cid = getKV(CLIENT_COOKIE_NAME) || undefined;
  if (!anonymousRoutes.includes(to.name || "") && cid && to.query.cid != cid) {
    if (to.query.cid && to.query.cid !== cid) {
      if (
        confirm(
          "The client you're trying to access is different from your current session. Switch to the client from the URL?"
        )
      )
        switchClient(to.query.cid as string);
    }
    await router.replace({
      ...to,
      query: {
        // This line is used to retain other query if there is any
        ...to.query,
        cid: cid,
      },
    } as RawLocation);
  }
});
